import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Conversation {
  id: string;
  title: string;
  model: string;
  created_at: string;
  updated_at: string;
}

export interface ConversationMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
}

export function useAIConversations() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_conversations')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setConversations(data || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
      toast.error('Erro ao carregar conversas');
    } finally {
      setIsLoading(false);
    }
  };

  const createConversation = async (title: string, model: string = 'google/gemini-2.5-flash') => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('ai_conversations')
        .insert({
          user_id: user.id,
          title,
          model,
        })
        .select()
        .single();

      if (error) throw error;

      setConversations(prev => [data, ...prev]);
      setCurrentConversation(data);
      setMessages([]);
      
      return data;
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Erro ao criar conversa');
      return null;
    }
  };

  const loadConversation = async (conversationId: string) => {
    try {
      // Load conversation details
      const { data: conversation, error: convError } = await supabase
        .from('ai_conversations')
        .select('*')
        .eq('id', conversationId)
        .single();

      if (convError) throw convError;
      setCurrentConversation(conversation);

      // Load messages
      const { data: msgs, error: msgsError } = await supabase
        .from('ai_conversation_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (msgsError) throw msgsError;
      
      // Cast role to the correct type
      const typedMessages = (msgs || []).map(msg => ({
        ...msg,
        role: msg.role as 'user' | 'assistant' | 'system'
      }));
      
      setMessages(typedMessages);
      return typedMessages;
    } catch (error) {
      console.error('Error loading conversation:', error);
      toast.error('Erro ao carregar conversa');
      return [];
    }
  };

  const saveMessage = async (conversationId: string, role: 'user' | 'assistant', content: string) => {
    try {
      const { data, error } = await supabase
        .from('ai_conversation_messages')
        .insert({
          conversation_id: conversationId,
          role,
          content,
        })
        .select()
        .single();

      if (error) throw error;

      // Update conversation's updated_at
      await supabase
        .from('ai_conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', conversationId);

      // Cast role to the correct type
      const typedMessage = {
        ...data,
        role: data.role as 'user' | 'assistant' | 'system'
      };

      setMessages(prev => [...prev, typedMessage]);
      return typedMessage;
    } catch (error) {
      console.error('Error saving message:', error);
      return null;
    }
  };

  const updateConversationTitle = async (conversationId: string, newTitle: string) => {
    try {
      const { error } = await supabase
        .from('ai_conversations')
        .update({ title: newTitle })
        .eq('id', conversationId);

      if (error) throw error;

      setConversations(prev =>
        prev.map(conv => conv.id === conversationId ? { ...conv, title: newTitle } : conv)
      );

      if (currentConversation?.id === conversationId) {
        setCurrentConversation(prev => prev ? { ...prev, title: newTitle } : null);
      }

      toast.success('Título atualizado');
    } catch (error) {
      console.error('Error updating title:', error);
      toast.error('Erro ao atualizar título');
    }
  };

  const deleteConversation = async (conversationId: string) => {
    console.log('🗑️ Attempting to delete conversation:', conversationId);
    
    try {
      const { data, error } = await supabase
        .from('ai_conversations')
        .delete()
        .eq('id', conversationId)
        .select();

      console.log('🗑️ Delete result:', { data, error });

      if (error) {
        console.error('❌ Delete error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      setConversations(prev => prev.filter(conv => conv.id !== conversationId));
      
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
      }

      console.log('✅ Conversation deleted successfully');
      toast.success('Conversa excluída com sucesso');
    } catch (error: any) {
      console.error('❌ Caught error deleting conversation:', error);
      const errorMessage = error?.message || 'Erro desconhecido';
      toast.error(`Erro ao excluir conversa: ${errorMessage}`);
    }
  };

  const generateConversationTitle = (firstMessage: string): string => {
    const maxLength = 50;
    if (firstMessage.length <= maxLength) {
      return firstMessage;
    }
    return firstMessage.substring(0, maxLength) + '...';
  };

  const groupConversationsByDate = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    return {
      today: conversations.filter(c => new Date(c.updated_at) >= today),
      lastSevenDays: conversations.filter(c => {
        const date = new Date(c.updated_at);
        return date < today && date >= sevenDaysAgo;
      }),
      lastThirtyDays: conversations.filter(c => {
        const date = new Date(c.updated_at);
        return date < sevenDaysAgo && date >= thirtyDaysAgo;
      }),
      older: conversations.filter(c => new Date(c.updated_at) < thirtyDaysAgo),
    };
  };

  return {
    conversations,
    currentConversation,
    messages,
    isLoading,
    loadConversations,
    createConversation,
    loadConversation,
    saveMessage,
    updateConversationTitle,
    deleteConversation,
    generateConversationTitle,
    groupConversationsByDate,
  };
}
